import { useState, useCallback } from 'react';
import { useSignInState } from '../Components/signInState';
import { dotnetBackendApiUrl } from '../config/dotnetBackendEnv';
import { getErrorMessage } from '../Helpers/utils';
import { bffRequestHeaders } from '../services/bffFetch';

interface useFetchProps {
  scope: string;
}

interface FetchAPIProps {
  url: string;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | undefined;
  body?: Record<string, unknown>;
}

interface UseFetchReturn {
  fetchAPI: (props: FetchAPIProps) => Promise<void>;
  isLoading: boolean;
  serverError: Error | null;
  apiData: Record<string, string | number | boolean | undefined> | null;
}

type FetchHeaders = Record<string, string>;

const useFetch = ({ scope: _scope }: useFetchProps): UseFetchReturn => {
  const apiUrl = dotnetBackendApiUrl;
  const { signInState } = useSignInState();

  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<Error | null>(null);
  const [apiData, setApiData] = useState<Record<string, string | number | boolean | undefined> | null>(null);

  const getAuthHeader = useCallback((): { headers: FetchHeaders } => ({
    headers: bffRequestHeaders(signInState.currentCustomerID),
  }), [signInState.currentCustomerID]);

  const fetchAPI = useCallback(async ({ url, method, body }: FetchAPIProps) => {
    setIsLoading(true);
    try {
      const header = getAuthHeader();
      const fullUrl = url.startsWith('http')
        ? url
        : url.startsWith('/')
          ? url
          : `${apiUrl}${url}`;
      const response = await fetch(fullUrl, {
        headers: header.headers,
        method: method,
        credentials: 'include',
        ...(body && { body: JSON.stringify(body) })
      });

      if (!response.ok) {
        const errorText = await response.text();
        const error = new Error(getErrorMessage(errorText));
        setServerError(error);
        throw error;
      }

      const data = await response.json();
      setApiData(data);
    } catch (error) {
      if (error instanceof Error) {
        setServerError(error);
      } else {
        const unknownError = new Error("An unknown error occurred");
        setServerError(unknownError);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, getAuthHeader]);

  return { fetchAPI, isLoading, serverError, apiData };
};

export default useFetch;
